import { MatDialog } from '@angular/material/dialog';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { tap, map, exhaustMap } from 'rxjs/operators';
import { FormGroup, FormControl } from '@angular/forms';
import { Pagination } from '@core/models/pagination.model';
import { SystemMaintenanceDataService } from './services/system-maintenance-data.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { SubheaderService } from '@core/_base/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import * as moment from 'moment-timezone';
import { SystemMaintenance } from '@core/models/system-maintenance.model';
import { SystemMaintenanceEditComponent } from './dialog/system-maintenance-edit.component';
import { Subscription, of } from 'rxjs';
import { AppPermissionService } from '@core/services/app-permission.service';

@Component({
  selector: 'kt-system-maintenance',
  templateUrl: './system-maintenance.component.html',
  styleUrls: ['./system-maintenance.component.scss']
})
export class SystemMaintenanceComponent implements OnInit, OnDestroy {

  form: FormGroup;

  ranges = this.transactionHttpService.ranges;

  dropdown = {
    perPage: this.dropdownHttpService.perPage
  };
  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
  pagination: Pagination;
  pageSize = 15;
  page = 1;
  maxSize = 5;
  params = '';

  systemMaintenances = [];
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;

  private subscriptions = new Subscription();

  // permissions
  canCreateSystemMaintenance: boolean;
  canEditSystemMaintenance: boolean;

  constructor(
    private subheaderService: SubheaderService,
    private loadingBar: LoadingBarService,
    private transactionHttpService: TransactionHttpService,
    private dropdownHttpService: DropdownHttpService,
    public dialog: MatDialog,
    private systemMaintenanceDataService: SystemMaintenanceDataService,
    private appPermissionService: AppPermissionService,
  ) { }

  ngOnInit() {
    this.formInit();
    this.onSubmit(true);

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canCreateSystemMaintenance = appPermissions.create_system_maintenance;
      this.canEditSystemMaintenance = appPermissions.edit_system_maintenance;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  onViewPageBy(page = 1) {
    this.systemMaintenances = [];
    this.loading = true;
    this.loadingBar.start();
    var params = this.params + `&page=${page}`;
    this.systemMaintenanceDataService.getWithQuery(`?paginate=1&perPage=${this.pageSize}${params}`).pipe(
      tap(res => {
        this.loading = false;
        this.systemMaintenances = res;
        this.pagination = this.systemMaintenanceDataService.pagination;
        this.loadingBar.complete();
      })
    ).subscribe();
  }

  onPerPage(size: Event) {
    this.page = 1;
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy();
  }

  onClearDate() {
    if (this.form.value.start_date !== null) {
      this.form.patchValue({ defaultDate: null });
    }
  }

  onDateRange(event: any) {
    if (event) {
      this.form.patchValue({
        start_date_time: event.startDate !== null && event.startDate !== undefined ? event.startDate : null,
        end_date_time: event.endDate !== null && event.endDate !== undefined ? event.endDate : null
      });
    }
  }

  onClear() {
    this.clearBtnLoading = true;
    this.form.patchValue({
      status: "",
      start_date_time: null,
      end_date_time: null,
      defaultDate: null,
    });
    this.onSubmit(true);
  }

  onSubmit(clearSearch?: boolean) {
    this.timezone = JSON.parse(localStorage.getItem('user_data')).timezone;
    this.searchBtnLoading = clearSearch ? false : true;
    this.systemMaintenances = [];
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        if (data['start_date_time']) {
          data['start_date_time'] = moment(data['start_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_date_time'] = moment(data['end_date_time']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        this.params = Object.keys(data).length > 0 ? '&' + Object.keys(data).map(key => key + '=' + data[key]).join('&') : '';
        this.loadingBar.start();
        return this.systemMaintenanceDataService.getWithQuery(`?paginate=1&perPage=${this.pageSize}${this.params}`).pipe(
          tap(res => {
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.page = 1;
            this.systemMaintenances = res;
            this.pagination = this.systemMaintenanceDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  onOpenDialog(mode: string, memberId?: number) {
    if (mode === 'create') {
      this.openDialogBy(SystemMaintenanceEditComponent, { mode: mode });
    }
    else {
      this.systemMaintenanceDataService.getById(memberId).subscribe(res => {
        this.openDialogBy(SystemMaintenanceEditComponent, { mode: mode, systemMaintenance: res });
      });
    }
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  private openDialogBy(componentRef: any, data: { mode: string, systemMaintenance?: SystemMaintenance }) {
    const dialogRef = this.dialog.open(componentRef, {
      width: '800px',
      height: 'auto',
      data
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onViewPageBy();
      }
    });
  }

  private filterFormFields(formData: any) {
    const fields = {};
    Object.keys(formData).forEach(key => {
      if (formData[key] !== '' && formData[key] !== null && key !== 'defaultDate') {
        fields[key] = formData[key];
      }
    });
    return fields;
  }

  private formInit() {
    this.form = new FormGroup({
      status: new FormControl(""),
      start_date_time: new FormControl(null),
      end_date_time: new FormControl(null),
      defaultDate: new FormControl(null),
    });
  }

}
// 